import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { PatientIdAiVerification, PatientIdDocument, PatientIdDocumentReview } from '../../types/api';
import { apiService } from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import { formatDateShort, formatDateTimeSG } from '../../utils/date';

type PatientIdDocumentViewerButtonProps = {
  visitULID: string;
  /** From queue — shown immediately while document loads. */
  patientName?: string;
  patientNric?: string;
  /** From queue API — highlights card when ID not yet vetted. */
  vettingPending?: boolean;
  onVettingComplete?: () => void;
};

/** Staff vetting is recorded only when <c>dateVetted</c> is set in the database. */
function isDocumentVetted(doc: PatientIdDocument): boolean {
  const d = doc.dateVetted?.trim();
  if (!d) {
    return false;
  }
  // Ignore sentinel / default SQL dates mistaken for "vetted".
  if (d.startsWith('0001-') || d.startsWith('0000-')) {
    return false;
  }
  return true;
}

function isVettingPending(doc: PatientIdDocument | null, queueHint: boolean): boolean {
  if (!doc) {
    return queueHint;
  }
  if (isDocumentVetted(doc)) {
    return false;
  }
  if (doc.isVettingPending === true) {
    return true;
  }
  return true;
}

function isReviewVetted(docs: PatientIdDocument[]): boolean {
  return docs.length > 0 && docs.every(isDocumentVetted);
}

function isReviewPending(docs: PatientIdDocument[], queueHint: boolean): boolean {
  if (docs.length === 0) return queueHint;
  return docs.some((d) => isVettingPending(d, false));
}

function isPdfDocument(doc: PatientIdDocument): boolean {
  const ext = (doc.extension ?? '').toLowerCase();
  return ext === '.pdf' || ext === 'pdf' || doc.fileName?.toLowerCase().endsWith('.pdf');
}

function isRejected(doc: PatientIdDocument): boolean {
  if (doc.vettingApproved === false) {
    return true;
  }
  if (doc.vettingApproved === true) {
    return false;
  }
  const remark = (doc.vettingRemark ?? '').trimStart();
  return remark.toLowerCase().startsWith('[rejected]');
}

function formatVettedAt(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return formatDateTimeSG(iso);
  } catch {
    return iso;
  }
}

function formatDateOfBirth(iso: string | null | undefined): string {
  if (!iso?.trim()) return '—';
  try {
    return formatDateShort(iso);
  } catch {
    return iso;
  }
}

function tick(ok: boolean | undefined, label: string) {
  if (ok === undefined) return null;
  return (
    <li>
      {ok ? '✓' : '✗'} {label}
    </li>
  );
}

function AiVerificationPanel({
  verification,
  pending,
}: {
  verification: PatientIdAiVerification;
  pending?: boolean;
}) {
  const status = (verification.status ?? '').toUpperCase();
  const advisoryPass = pending && status === 'PASS';
  const statusClass =
    status === 'PASS'
      ? 'patient-id-vet-ai__status--pass'
      : status === 'FAIL'
        ? 'patient-id-vet-ai__status--fail'
        : 'patient-id-vet-ai__status--review';
  const matches = verification.fieldMatches ?? {};
  const reasons = [...(verification.criticalFailures ?? []), ...(verification.reasons ?? [])];
  const statusLabel = advisoryPass
    ? 'AI RECOMMENDS APPROVE'
    : status === 'PASS'
      ? 'VERIFIED'
      : status === 'FAIL'
        ? 'NOT VERIFIED'
        : 'MANUAL REVIEW REQUIRED';

  return (
    <div className="patient-id-vet-ai" aria-label="AI identity verification evidence">
      <p className="patient-id-vet-ai__heading">
        {pending ? 'AI check on this upload (staff confirmation required)' : 'AI-assisted verification (evidence only)'}
      </p>
      <p className={`patient-id-vet-ai__status ${statusClass}`}>
        {statusLabel}
        {typeof verification.score === 'number' ? ` · score ${verification.score}` : ''}
        {verification.fraudRisk ? ` · fraud ${verification.fraudRisk}` : ''}
      </p>
      {pending ? (
        <p className="patient-id-vet-ai__advisory">
          {advisoryPass
            ? 'AI recommends approve, but staff must still confirm this re-upload.'
            : 'Compare this summary with the photos below, then approve or reject.'}
        </p>
      ) : null}
      <ul className="patient-id-vet-ai__ticks">
        {tick(verification.documentTypeMatch ?? verification.submittedDocumentType === verification.detectedDocumentType, `Document type (${verification.detectedDocumentType || 'unknown'})`)}
        {tick(matches.full_name?.match ?? matches.fullName?.match, 'Name match')}
        {tick(matches.date_of_birth?.match ?? matches.dateOfBirth?.match, 'Date of birth match')}
        {tick(matches.id_number?.match ?? matches.idNumber?.match, 'ID number match')}
        {tick(verification.documentValid, 'Document valid')}
        {tick(verification.fraudRisk?.toUpperCase() === 'LOW', 'Low fraud risk')}
      </ul>
      {reasons.length > 0 ? (
        <ul className="patient-id-vet-ai__reasons">
          {reasons.slice(0, 6).map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function PatientIdentityPanel({
  fullName,
  nric,
  dateOfBirth,
}: {
  fullName?: string | null;
  nric?: string | null;
  dateOfBirth?: string | null;
}) {
  const name = fullName?.trim() || '—';
  const idNo = nric?.trim() || '—';
  const dob = formatDateOfBirth(dateOfBirth);

  return (
    <div className="patient-id-vet-identity" aria-label="Registered patient details">
      <p className="patient-id-vet-identity__heading">Registered details — verify against ID below</p>
      <dl className="patient-id-vet-identity__grid">
        <div className="patient-id-vet-identity__row">
          <dt>Full name</dt>
          <dd>{name}</dd>
        </div>
        <div className="patient-id-vet-identity__row">
          <dt>Date of birth</dt>
          <dd>{dob}</dd>
        </div>
        <div className="patient-id-vet-identity__row">
          <dt>NRIC / ID</dt>
          <dd>{idNo}</dd>
        </div>
      </dl>
    </div>
  );
}

/** Queue card action: view portal Patient ID and approve/reject (med.CustomerAttachment vetting). */
export function PatientIdDocumentViewerButton({
  visitULID,
  patientName,
  patientNric,
  vettingPending = false,
  onVettingComplete,
}: PatientIdDocumentViewerButtonProps) {
  const [open, setOpen] = useState(false);
  const [review, setReview] = useState<PatientIdDocumentReview | null>(null);
  const [docBlobUrls, setDocBlobUrls] = useState<Record<string, string>>({});
  const [metaLoading, setMetaLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);
  const [remark, setRemark] = useState('');
  const [vetSubmitting, setVetSubmitting] = useState(false);
  const [vetError, setVetError] = useState<string | null>(null);
  const [staffUploadFile, setStaffUploadFile] = useState<File | null>(null);
  const [staffUploadBackFile, setStaffUploadBackFile] = useState<File | null>(null);
  const [staffUploading, setStaffUploading] = useState(false);
  const [staffUploadError, setStaffUploadError] = useState<string | null>(null);

  const documents = review?.documents ?? [];
  const primaryDoc = documents[0] ?? null;
  const pending = isReviewPending(documents, vettingPending);
  const vetted = isReviewVetted(documents);
  const showStaffUpload = pending && documents.length === 0;
  const showVetActions = primaryDoc && pending && !vetSubmitting;
  const displayName = review?.patientFullName?.trim() || patientName?.trim() || '';
  const displayNric = review?.patientNric?.trim() || patientNric?.trim() || '';
  const displayDob = review?.patientDateOfBirth ?? null;

  const revokeBlobUrls = useCallback((urls: Record<string, string>) => {
    Object.values(urls).forEach((url) => URL.revokeObjectURL(url));
  }, []);

  useEffect(() => {
    return () => revokeBlobUrls(docBlobUrls);
  }, [docBlobUrls, revokeBlobUrls]);

  const handleClose = () => {
    setOpen(false);
    setReview(null);
    setDocBlobUrls((prev) => {
      revokeBlobUrls(prev);
      return {};
    });
    setContentError(null);
    setVetError(null);
    setRemark('');
    setMetaLoading(false);
    setContentLoading(false);
    setVetSubmitting(false);
    setStaffUploadFile(null);
    setStaffUploadBackFile(null);
    setStaffUploading(false);
    setStaffUploadError(null);
  };

  const loadDocument = async () => {
    setContentError(null);
    setVetError(null);
    setReview(null);
    setDocBlobUrls((prev) => {
      revokeBlobUrls(prev);
      return {};
    });
    setMetaLoading(true);

    try {
      const loaded = await apiService.getPatientIdDocumentReview(visitULID);
      if (!loaded || loaded.documents.length === 0) {
        if (vettingPending) {
          setReview({
            documents: [],
            patientFullName: patientName ?? null,
            patientNric: patientNric ?? null,
            patientDateOfBirth: null,
          });
          return;
        }
        setContentError('No Patient ID document on file for this patient.');
        return;
      }
      setReview(loaded);
      setMetaLoading(false);
      setContentLoading(true);

      const urls: Record<string, string> = {};
      for (const doc of loaded.documents) {
        const blob = await apiService.getPatientIdDocumentContent(visitULID, doc.attachmentUlid);
        urls[doc.attachmentUlid] = URL.createObjectURL(blob);
      }
      setDocBlobUrls(urls);
    } catch (e) {
      setContentError(getApiErrorMessage(e, 'Could not load ID document.'));
    } finally {
      setMetaLoading(false);
      setContentLoading(false);
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    setRemark('');
    await loadDocument();
  };

  const handleVet = async (approved: boolean) => {
    if (!primaryDoc) return;
    setVetError(null);

    if (!approved && !remark.trim()) {
      setVetError('Please enter a remark when rejecting the ID document.');
      return;
    }

    setVetSubmitting(true);
    try {
      await apiService.vetPatientIdDocument(visitULID, {
        attachmentUlid: primaryDoc.attachmentUlid,
        approved,
        vettingRemark: remark.trim() || undefined,
      });
      setRemark('');
      onVettingComplete?.();
      await loadDocument();
    } catch (e) {
      setVetError(getApiErrorMessage(e, 'Could not save ID verification. Please try again.'));
    } finally {
      setVetSubmitting(false);
    }
  };

  const handleStaffUpload = async () => {
    if (!staffUploadFile) {
      setStaffUploadError('Please choose an ID document file.');
      return;
    }
    setStaffUploadError(null);
    setStaffUploading(true);
    try {
      await apiService.uploadPatientIdDocumentOnBehalf(
        visitULID,
        staffUploadFile,
        staffUploadBackFile ?? undefined,
      );
      setStaffUploadFile(null);
      setStaffUploadBackFile(null);
      onVettingComplete?.();
      await loadDocument();
    } catch (e) {
      setStaffUploadError(getApiErrorMessage(e, 'Could not upload Patient ID document.'));
    } finally {
      setStaffUploading(false);
    }
  };

  const modal =
    open && typeof window !== 'undefined' ? (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Patient ID document"
        className="patient-id-vet-modal-backdrop"
        onClick={handleClose}
      >
        <div className="patient-id-vet-modal" onClick={(e) => e.stopPropagation()}>
          <div className="patient-id-vet-modal__header">
            <div>
              <strong className="patient-id-vet-modal__title">Verify Patient ID</strong>
              {documents.length > 1 ? (
                <div className="patient-id-vet-modal__subtitle">
                  {documents.length} pages — {documents.map((d) => d.sideLabel ?? 'ID').join(' & ')}
                </div>
              ) : primaryDoc?.fileName ? (
                <div className="patient-id-vet-modal__subtitle">{primaryDoc.fileName}</div>
              ) : null}
            </div>
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Close
            </button>
          </div>

          {primaryDoc && vetted && (
            <div
              className={`patient-id-vet-banner ${
                isRejected(primaryDoc) ? 'patient-id-vet-banner--rejected' : 'patient-id-vet-banner--approved'
              }`}
            >
              {isRejected(primaryDoc) ? 'Rejected' : 'Approved'}
              {primaryDoc.vettedByName ? ` by ${primaryDoc.vettedByName}` : ''}
              {primaryDoc.dateVetted ? ` · ${formatVettedAt(primaryDoc.dateVetted)}` : ''}
              {primaryDoc.vettingRemark ? (
                <div className="patient-id-vet-banner__remark">{primaryDoc.vettingRemark}</div>
              ) : null}
            </div>
          )}

          {primaryDoc && pending && (
            <div className="patient-id-vet-banner patient-id-vet-banner--pending">
              Compare the registered details below with the ID document photo.
            </div>
          )}

          {(displayName || displayNric || displayDob || patientName || patientNric || primaryDoc) && (
            <PatientIdentityPanel
              fullName={displayName || patientName}
              nric={displayNric || patientNric}
              dateOfBirth={displayDob}
            />
          )}

          {review?.aiVerification ? (
            <AiVerificationPanel verification={review.aiVerification} pending={pending} />
          ) : null}

          <div className="patient-id-vet-modal__body">
            {(metaLoading || contentLoading) && !contentError && documents.length === 0 && !showStaffUpload && (
              <p className="patient-id-vet-modal__status">Loading document…</p>
            )}
            {contentError && <p className="patient-id-vet-modal__error">{contentError}</p>}
            {showStaffUpload && (
              <div className="patient-id-vet-staff-upload">
                <p className="patient-id-vet-modal__status">
                  No Patient ID on file yet. Upload on behalf of the patient (e.g. from chat), then verify below.
                </p>
                <label className="patient-id-vet-modal__label" htmlFor={`staff-id-front-${visitULID}`}>
                  ID document (front or single page)
                </label>
                <input
                  id={`staff-id-front-${visitULID}`}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  disabled={staffUploading}
                  onChange={(e) => setStaffUploadFile(e.target.files?.[0] ?? null)}
                />
                <label className="patient-id-vet-modal__label" htmlFor={`staff-id-back-${visitULID}`}>
                  Back of ID (optional)
                </label>
                <input
                  id={`staff-id-back-${visitULID}`}
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  disabled={staffUploading}
                  onChange={(e) => setStaffUploadBackFile(e.target.files?.[0] ?? null)}
                />
                {staffUploadError ? <p className="patient-id-vet-modal__error">{staffUploadError}</p> : null}
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={staffUploading || !staffUploadFile}
                  onClick={() => void handleStaffUpload()}
                >
                  {staffUploading ? 'Uploading…' : 'Upload Patient ID'}
                </button>
              </div>
            )}
            {!metaLoading && !contentLoading && !contentError &&
              documents.map((doc) => {
                const blobUrl = docBlobUrls[doc.attachmentUlid];
                if (!blobUrl) return null;
                const label = doc.sideLabel ?? 'ID';
                return (
                  <div key={doc.attachmentUlid} className="patient-id-vet-doc-panel">
                    <p className="patient-id-vet-doc-panel__label">{label}</p>
                    {isPdfDocument(doc) ? (
                      <iframe
                        title={`Patient ID ${label}`}
                        src={blobUrl}
                        className="patient-id-vet-modal__iframe"
                      />
                    ) : (
                      <img src={blobUrl} alt={`Patient ID ${label}`} className="patient-id-vet-modal__image" />
                    )}
                  </div>
                );
              })}
          </div>

          {showVetActions && (
            <div className="patient-id-vet-modal__footer">
              <label className="patient-id-vet-modal__label" htmlFor={`vet-remark-${visitULID}`}>
                Remarks (required if rejecting)
              </label>
              <textarea
                id={`vet-remark-${visitULID}`}
                className="patient-id-vet-modal__textarea"
                rows={2}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Optional for approval; required for rejection"
                disabled={vetSubmitting}
              />
              {vetError ? <p className="patient-id-vet-modal__error">{vetError}</p> : null}
              <div className="patient-id-vet-modal__actions">
                <button
                  type="button"
                  className="btn patient-id-vet-btn patient-id-vet-btn--reject"
                  disabled={vetSubmitting}
                  onClick={() => void handleVet(false)}
                >
                  {vetSubmitting ? 'Saving…' : 'Reject'}
                </button>
                <button
                  type="button"
                  className="btn patient-id-vet-btn patient-id-vet-btn--approve"
                  disabled={vetSubmitting}
                  onClick={() => void handleVet(true)}
                >
                  {vetSubmitting ? 'Saving…' : 'Approve'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    ) : null;

  const btnClass = [
    'staff-tele-btn',
    'staff-tele-btn--viewid',
    vettingPending ? 'staff-tele-btn--viewid-pending' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <button
        type="button"
        className={btnClass}
        onClick={(e) => {
          e.stopPropagation();
          void handleOpen();
        }}
        title={
          vettingPending
            ? 'Patient ID awaiting verification — click to review'
            : 'View NRIC/passport uploaded at registration'
        }
      >
        {vettingPending ? 'Verify ID' : 'View ID'}
      </button>
      {modal && createPortal(modal, window.document.body)}
    </>
  );
}
